import { useEffect, useState } from 'react';
import { useBrand } from '../../brand/BrandProvider';
import { usePackages, usePricesByPackage } from '../../api/catalog';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Building2, Check, CreditCard, Loader2 } from 'lucide-react';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Card } from '../../components/Card';
import { Banner } from '../../components/Banner';
import { Stepper } from '../../components/Stepper';
import { PageHeader } from '../../components/PageHeader';
import { Money } from '../../components/Money';
import { cn } from '../../lib/cn';
import { apiPost } from '../../api/client';
import { startCheckout } from '../../api/billing';

const STEPS = ['Business details', 'Choose a plan', 'Review & pay'];

/**
 * Add a new business — §5.10
 * An existing owner adds another shop: create the business (shared DB, owner =
 * the signed-in user — enforced server-side), then pay the first invoice via
 * the hosted SSLCommerz page. If the gateway hand-off fails the invoice stays
 * open on /billing.
 */
export function NewBusiness() {
  const navigate = useNavigate();
  const brand = useBrand();
  const [step, setStep] = useState(0);

  // step 1
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');

  // step 2 — plan catalog from the API (published + public packages)
  const { data: packages = [] } = usePackages();
  const { data: prices = [] } = usePricesByPackage();
  const sellable = packages.filter((p) => p.status === 'published' && p.visibility === 'public');
  const [interval, setInterval] = useState<'month' | 'year'>('month');
  const [planId, setPlanId] = useState('');
  useEffect(() => {
    const first = sellable[0];
    if (!planId && first) setPlanId(first.id);
  }, [sellable, planId]);

  const priceFor = (pkgId: string, iv: 'month' | 'year') =>
    prices.find((pr) => pr.packageId === pkgId && pr.interval === iv && pr.status === 'active');
  const selectedPkg = sellable.find((p) => p.id === planId);
  const selectedPrice = priceFor(planId, interval) ?? priceFor(planId, 'month');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subdomainValid = subdomain === '' || /^[a-z0-9-]{3,}$/.test(subdomain);
  const stepValid =
    step === 0 ? name.trim().length >= 2 && subdomainValid : step === 1 ? !!selectedPrice : true;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (step < STEPS.length - 1) {
      if (stepValid) setStep((s) => s + 1);
      return;
    }
    if (!selectedPrice) return;
    setSubmitting(true);
    setError(null);
    let createdId: string | null = null;
    try {
      const created = await apiPost<{ id: string; name: string; status: string }>('/businesses', {
        name: name.trim(),
        subtitle: subdomain.trim() || undefined,
        priceId: selectedPrice.id,
        paymentMode: 'online',
        trialDays: 0,
      });
      createdId = created.id;
    } catch {
      setError('Could not create the business. Please try again.');
      setSubmitting(false);
      return;
    }
    try {
      const session = await startCheckout({ businessId: createdId, priceId: selectedPrice.id });
      window.location.href = session.gatewayPageURL;
    } catch {
      // Business exists with an open invoice — send the owner to Billing to pay.
      navigate({ to: '/billing' });
    }
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8 max-w-3xl mx-auto">
      <PageHeader
        eyebrow="Account"
        title="Add a new business"
        subtitle="Each business gets its own subscription and POS."
      />

      <div className="mb-8 flex justify-center">
        <Stepper steps={STEPS} current={step} />
      </div>

      <Card className="p-5 sm:p-8">
        <form onSubmit={onSubmit} className="space-y-6">
          {step === 0 && (
            <div className="space-y-5">
              <Input
                label="Business name"
                required
                placeholder="e.g. My Shop — Dhaka"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <Input
                label="POS subdomain"
                placeholder="myshop"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                hint="Optional — lowercase letters, digits and dashes (min 3). Leave blank to derive from the name."
                error={subdomainValid ? undefined : 'Lowercase letters, digits and dashes only (min 3 characters).'}
              />
              <Banner tone="info">
                The new business is created under your account — you stay the owner and can switch between
                businesses from the top bar.
              </Banner>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-5">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">Billing interval</div>
                <div className="inline-flex rounded-md border border-zinc-300 dark:border-zinc-700 overflow-hidden">
                  {(['month', 'year'] as const).map((iv) => (
                    <button
                      key={iv}
                      type="button"
                      onClick={() => setInterval(iv)}
                      className={cn(
                        'px-3 py-1.5 text-xs font-medium transition',
                        interval === iv
                          ? 'bg-brand text-white'
                          : 'bg-white dark:bg-zinc-900 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-50 dark:hover:bg-zinc-800',
                      )}
                    >
                      {iv === 'month' ? 'Monthly' : 'Yearly'}
                    </button>
                  ))}
                </div>
              </div>

              {sellable.length === 0 ? (
                <div className="p-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
                  <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                  Loading plans…
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {sellable.map((p) => {
                    const price = priceFor(p.id, interval);
                    const active = planId === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setPlanId(p.id)}
                        className={cn(
                          'text-left p-4 rounded-lg border transition',
                          active
                            ? 'border-brand ring-2 ring-brand/30 bg-brand/5'
                            : 'border-zinc-200 dark:border-zinc-700 hover:border-zinc-300 dark:hover:border-zinc-600',
                        )}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-zinc-950 dark:text-zinc-50">{p.name}</span>
                          {active && <Check className="w-4 h-4 text-brand" />}
                        </div>
                        <div className="mt-1.5 text-sm">
                          {price ? (
                            <>
                              <Money minor={price.amount} hideCents tone="neutral" className="font-medium text-zinc-950 dark:text-zinc-50" />
                              <span className="text-xs text-zinc-500 dark:text-zinc-400"> /{interval === 'month' ? 'mo' : 'yr'}</span>
                            </>
                          ) : (
                            <span className="text-xs text-zinc-500 dark:text-zinc-400">Not available {interval === 'month' ? 'monthly' : 'yearly'}</span>
                          )}
                        </div>
                        {p.tagline && (
                          <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400 line-clamp-2">{p.tagline}</div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-700">
                <ReviewRow icon={<Building2 className="w-4 h-4" />} label="Business" value={name.trim()} />
                <ReviewRow
                  icon={<Building2 className="w-4 h-4" />}
                  label="POS subdomain"
                  value={subdomain.trim() || 'derived from the name'}
                />
                <ReviewRow
                  icon={<CreditCard className="w-4 h-4" />}
                  label="Plan"
                  value={
                    <>
                      {selectedPkg?.name ?? '—'} · {interval === 'month' ? 'monthly' : 'yearly'} ·{' '}
                      {selectedPrice ? <Money minor={selectedPrice.amount} hideCents tone="neutral" className="font-medium" /> : '—'}
                    </>
                  }
                />
              </div>

              <Banner tone="info" title="What happens next">
                Your business is created immediately and you're taken to the secure {brand.name} payment page
                (SSLCommerz) to pay the first {interval === 'month' ? 'month' : 'year'}. If you don't complete the
                payment, the invoice stays open under <strong>Billing</strong> and you can pay any time.
              </Banner>

              {error && <Banner tone="error" title="Couldn’t create the business">{error}</Banner>}
            </div>
          )}

          <div className="flex items-center justify-between pt-2">
            {step > 0 ? (
              <Button type="button" variant="ghost" iconLeft={<ArrowLeft className="w-4 h-4" />} onClick={() => setStep((s) => s - 1)} disabled={submitting}>
                Back
              </Button>
            ) : (
              <span />
            )}
            <Button type="submit" loading={submitting} disabled={!stepValid || submitting} iconRight={step < STEPS.length - 1 ? <ArrowRight className="w-4 h-4" /> : undefined}>
              {step < STEPS.length - 1 ? 'Continue' : 'Create & pay'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function ReviewRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <span className="w-7 h-7 grid place-items-center rounded-md bg-zinc-100 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 flex-shrink-0">
        {icon}
      </span>
      <span className="text-xs uppercase tracking-wider font-medium text-zinc-500 dark:text-zinc-400 w-32 flex-shrink-0">{label}</span>
      <span className="text-sm text-zinc-950 dark:text-zinc-50 min-w-0">{value}</span>
    </div>
  );
}
